<script setup lang="ts">
  import { computed } from "vue";
  import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
  import { useAnalysisQuery } from "@/composables/useAnalysisQuery";
  import type { CrosstabBand, CrosstabCell, CrosstabColumn, CrosstabData } from "@/types";
  import { useAnalysisContext } from "../analysisData";
  import AnalysisCardShell from "./AnalysisCardShell.vue";

  const props = defineProps({
    card: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
  });

  // Follow the dashboard's weighting + locale so the crosstab matches the rest of the view.
  const { weighted, displayLocale } = useAnalysisContext();

  const rows = computed(() => props.card.content?.rows || []);
  const columns = computed(() => props.card.content?.columns || []);
  const hasSelection = computed(() => rows.value.length > 0 && columns.value.length > 0);
  const showCounts = computed(() => props.card.content?.show_counts !== false);

  const body = computed(() => {
    if (!hasSelection.value) return null;
    return {
      rows: rows.value,
      columns: columns.value,
      significance: props.card.content?.significance || {},
      weighted: weighted.value,
      locale: displayLocale.value,
    };
  });

  const { result, loading, state: queryState } = useAnalysisQuery<CrosstabData>("crosstabs", body);
  const state = computed(() => queryState((result.value?.bands?.length ?? 0) > 0));

  type FlatColumn = CrosstabColumn & { firstInBand: boolean };

  // Flat list of columns across bands, flagged with firstInBand for the band dividers.
  const flatColumns = computed<FlatColumn[]>(() =>
    (result.value?.bands || []).flatMap((band) => band.columns.map((col, i) => ({ ...col, firstInBand: i === 0 }))),
  );

  function bandLabel(band: CrosstabBand) {
    return band.kind === "populations" ? "Populations" : band.name;
  }

  function fmtPct(value: number | null | undefined) {
    return value == null ? "–" : `${Math.round(value * 100)}%`;
  }

  function fmtSummary(value: number | string | null | undefined) {
    return value == null ? "–" : value;
  }

  function letters(cell: CrosstabCell | undefined) {
    if (!cell) return "";
    const parts = [];
    if (cell.superior_to?.length) parts.push(`▲${cell.superior_to.join("")}`);
    if (cell.inferior_to?.length) parts.push(`▼${cell.inferior_to.join("")}`);
    return parts.join(" ");
  }

  const VALUE_CELL = "border-b px-2 py-1.5 text-right tabular-nums group-hover:bg-muted/40";
  const PILL = "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5";

  function pillClass(cell: CrosstabCell | undefined) {
    if (cell?.sig === "superior") return `${PILL} bg-status-success-soft text-status-success-strong`;
    if (cell?.sig === "inferior") return `${PILL} bg-status-error-soft text-status-error-strong`;
    return "";
  }

  function lettersClass(cell: CrosstabCell | undefined) {
    if (cell?.sig === "superior") return "text-status-success-strong";
    if (cell?.sig === "inferior") return "text-status-error-strong";
    return "text-muted-foreground";
  }

  function dividerClass(col: { firstInBand: boolean }) {
    return col.firstInBand ? "border-l" : "";
  }
</script>

<template>
  <AnalysisCardShell
    :card="card"
    :readonly="readonly"
    :weighted="weighted"
    :state="state"
    state-message="Select at least one question for rows and one column to build the crosstab.">
    <div :class="{ 'opacity-50': loading }">
      <div class="max-h-[70vh] overflow-auto print:max-h-none print:overflow-visible">
        <Table class="border-separate border-spacing-0 text-xs print:break-inside-auto">
          <TableHeader>
            <TableRow class="hover:bg-transparent">
              <TableHead rowspan="2" class="sticky left-0 top-0 z-30 h-8 min-w-[10rem] border-b bg-background px-2 text-xs font-medium" />
              <TableHead class="sticky top-0 z-20 h-8 border-b border-l bg-background px-2" />
              <TableHead
                v-for="band in result.bands"
                :key="`band-${band.kind}-${band.question_id}`"
                :colspan="band.columns.length"
                class="sticky top-0 z-20 h-8 border-b border-l bg-background px-2 text-center text-xs font-semibold">
                {{ bandLabel(band) }}
              </TableHead>
            </TableRow>
            <TableRow class="hover:bg-transparent">
              <TableHead class="sticky top-8 z-20 h-auto w-[120px] border-b border-l bg-background px-2 py-1.5 text-right align-bottom text-xs font-medium whitespace-normal">
                <span class="flex items-center justify-end gap-1.5">
                  <span class="inline-flex items-center justify-center rounded border border-border px-1.5 py-0.5 text-3xs font-semibold leading-none text-muted-foreground">A</span>
                  Global
                </span>
              </TableHead>
              <template v-for="band in result.bands" :key="`cols-${band.kind}-${band.question_id}`">
                <TableHead
                  v-for="(col, i) in band.columns"
                  :key="col.letter"
                  class="sticky top-8 z-20 h-auto w-[120px] border-b bg-background px-2 py-1.5 text-right align-bottom text-xs font-medium whitespace-normal"
                  :class="dividerClass({ firstInBand: i === 0 })">
                  <span class="flex items-start justify-end gap-1.5">
                    <span class="mt-px inline-flex shrink-0 items-center justify-center rounded border border-border px-1.5 py-0.5 text-3xs font-semibold leading-none text-muted-foreground">
                      {{ col.letter }}
                    </span>
                    <span class="line-clamp-2 text-right leading-tight" :title="col.name">{{ col.name }}</span>
                  </span>
                </TableHead>
              </template>
            </TableRow>
          </TableHeader>

          <TableBody>
            <template v-for="row in result.rows" :key="row.question_id">
              <TableRow class="bg-muted/30 hover:bg-muted/30 print:break-inside-avoid">
                <TableCell :colspan="flatColumns.length + 2" class="border-b p-0">
                  <div class="sticky left-0 flex w-0 min-w-full items-baseline gap-2 px-2 py-1.5">
                    <span class="shrink-0 font-semibold">{{ row.name }}</span>
                    <span v-if="row.title" class="line-clamp-2 min-w-0 text-muted-foreground" :title="row.title">{{ row.title }}</span>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow class="group text-muted-foreground hover:bg-transparent print:break-inside-avoid">
                <TableCell class="sticky left-0 z-10 border-b bg-background px-2 py-1 group-hover:bg-muted">N</TableCell>
                <TableCell class="border-b border-l px-2 py-1 text-right tabular-nums group-hover:bg-muted/40">
                  {{ row.global.respondents }}
                </TableCell>
                <TableCell
                  v-for="col in flatColumns"
                  :key="`n-${row.question_id}-${col.letter}`"
                  class="border-b px-2 py-1 text-right tabular-nums group-hover:bg-muted/40"
                  :class="dividerClass(col)">
                  {{ col.respondents?.[row.question_id] ?? 0 }}
                </TableCell>
              </TableRow>
              <TableRow
                v-for="answer in row.answers"
                :key="`${row.question_id}-${answer.code}`"
                class="group hover:bg-transparent print:break-inside-avoid">
                <TableCell class="sticky left-0 z-10 border-b bg-background px-2 py-1.5 font-medium whitespace-normal group-hover:bg-muted">
                  <span class="line-clamp-2 max-w-[18rem] break-words" :title="answer.title">{{ answer.title }}</span>
                </TableCell>
                <TableCell class="border-l" :class="VALUE_CELL">
                  <span class="block">{{ fmtPct(answer.global.percentage) }}</span>
                  <span v-if="showCounts" class="block text-3xs text-muted-foreground">n={{ answer.global.n }}</span>
                </TableCell>
                <TableCell
                  v-for="col in flatColumns"
                  :key="`${row.question_id}-${answer.code}-${col.letter}`"
                  :class="[VALUE_CELL, dividerClass(col)]">
                  <span :class="pillClass(answer.cells[col.letter])">
                    {{ fmtPct(answer.cells[col.letter]?.percentage) }}
                    <span v-if="answer.cells[col.letter]?.sig === 'superior'" class="text-3xs">▲</span>
                    <span v-else-if="answer.cells[col.letter]?.sig === 'inferior'" class="text-3xs">▼</span>
                  </span>
                  <span v-if="showCounts" class="block text-3xs text-muted-foreground">n={{ answer.cells[col.letter]?.n ?? 0 }}</span>
                  <span
                    v-if="letters(answer.cells[col.letter])"
                    class="block text-3xs font-medium leading-tight"
                    :class="lettersClass(answer.cells[col.letter])">
                    {{ letters(answer.cells[col.letter]) }}
                  </span>
                </TableCell>
              </TableRow>
              <TableRow v-if="row.summary" :key="`summary-${row.question_id}`" class="group font-medium hover:bg-transparent print:break-inside-avoid">
                <TableCell class="sticky left-0 z-10 border-b bg-background px-2 py-1.5 group-hover:bg-muted">
                  {{ row.summary.label }}
                </TableCell>
                <TableCell class="border-l" :class="VALUE_CELL">
                  {{ fmtSummary(row.summary.global) }}
                </TableCell>
                <TableCell
                  v-for="col in flatColumns"
                  :key="`summary-${row.question_id}-${col.letter}`"
                  :class="[VALUE_CELL, dividerClass(col)]">
                  {{ fmtSummary(row.summary.cells[col.letter]) }}
                </TableCell>
              </TableRow>
            </template>
          </TableBody>
        </Table>
      </div>

      <div class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-muted-foreground">
        <span class="flex items-center gap-1">
          <span class="inline-block h-3 w-3 rounded-sm bg-status-success-soft ring-1 ring-status-success-border" />
          Significantly higher
        </span>
        <span class="flex items-center gap-1">
          <span class="inline-block h-3 w-3 rounded-sm bg-status-error-soft ring-1 ring-status-error-border" />
          Significantly lower
        </span>
        <span>▲/▼ letters = vs columns &amp; global (A)</span>
        <span v-if="result.significance">at {{ Math.round(result.significance.level * 100) }}% confidence</span>
      </div>
    </div>
  </AnalysisCardShell>
</template>
